import React from 'react';
import type { MigrationStage } from '../../types/logs';

interface StageFilterBarProps {
  /** Currently selected stages */
  selectedStages: MigrationStage[];
  /** Callback when stage selection changes */
  onStageChange: (stages: MigrationStage[]) => void;
  /** Stages that have logs available */
  availableStages?: MigrationStage[];
  /** Optional className for customization */
  className?: string;
}

const ALL_STAGES: MigrationStage[] = ['Export', 'Transform', 'Import'];

/**
 * Stage filter bar - allows users to filter logs by migration stage
 * Supports multiple selection with visual checkmarks
 */
export const StageFilterBar: React.FC<StageFilterBarProps> = ({
  selectedStages,
  onStageChange,
  availableStages,
  className = ''
}) => {
  const toggleStage = (stage: MigrationStage) => {
    // Don't toggle if stage is not available
    if (availableStages && !availableStages.includes(stage)) {
      return;
    }

    if (selectedStages.includes(stage)) {
      // Remove stage from selection
      onStageChange(selectedStages.filter((s) => s !== stage));
    } else {
      // Add stage to selection
      onStageChange([...selectedStages, stage]);
    }
  };

  const selectAllStages = () => {
    onStageChange([]);
  };

  const isAllSelected = selectedStages.length === 0;

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 self-center">
        Stage Filters:
      </span>

      {ALL_STAGES.map((stage) => {
        const isSelected = selectedStages.includes(stage);
        const isAvailable = !availableStages || availableStages.includes(stage);
        const isDisabled = !isAvailable;

        return (
          <button
            key={stage}
            onClick={() => toggleStage(stage)}
            disabled={isDisabled}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${
                isDisabled
                  ? 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed opacity-50'
                  : isSelected
                    ? 'bg-green-600 text-white dark:bg-green-700 hover:bg-green-700 dark:hover:bg-green-600'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            `}
            aria-pressed={isSelected}
            aria-disabled={isDisabled}
            title={isDisabled ? `No ${stage} logs available` : undefined}
          >
            {isSelected && <span className="mr-1.5">✓</span>}
            {stage}
          </button>
        );
      })}

      <button
        onClick={selectAllStages}
        className={`
          px-4 py-2 rounded-lg text-sm font-medium transition-colors
          ${
            isAllSelected
              ? 'bg-green-600 text-white dark:bg-green-700 hover:bg-green-700 dark:hover:bg-green-600'
              : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        `}
        aria-pressed={isAllSelected}
      >
        {isAllSelected && <span className="mr-1.5">✓</span>}
        All Stages
      </button>
    </div>
  );
};
